import { LinearProgress, Radio } from '@mui/material'
import { styled } from '@mui/material/styles'
import BackComponent from 'components/back'
import ButtonMobileComponent from 'components/button-mobile'
import { AssignmentIndIcon, DeleteIcon, WebsiteLinkIcon } from 'components/icons'
import InputMobileComponent from 'components/input-mobile'
import ModalConfirmRemoveComponent from 'components/modal-confirm-remove'
import NavComponent from 'components/nav'
import SelectMobileComponent from 'components/select-mobile'
import ToastComponent from 'components/toast'
import WizardComponent from 'components/wizard'
import React, { useEffect, useState } from 'react'
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom'
import { MessageProps } from 'types/CommonTypes'
import { StudentProps } from 'types/StudentTypes'
import { isWeb, titleCaseSchoolNameForUI } from 'utils/constants'
import { deleteStudent, updateStudentSettings } from 'utils/students'
import { isValidName } from 'utils/validations'
import Arrow from '../../images/arrow_forward.png'
import './index.css'
import { PlusIcon } from 'components/icons/PlusIcon'

const CustomRadio = styled(Radio)(({ theme }) => ({
  marginLeft: '4px',
  color: '#A3A3A3', // Default color when not checked
  width: '16px',
  '&.Mui-checked': {
    color: '#E5C265', // Color when checked
  },
}))

function RegistrationComponent({
  studentIdFromQuery,
  students,
  onSelect,
  onRemove,
}: {
  studentIdFromQuery: string
  students: StudentProps[]
  onSelect: (ssi: string, cb?: () => void) => void
  onRemove: (ssi: string, cb?: () => void) => void
}): JSX.Element {
  // 1. State Hooks
  const navigate: NavigateFunction = useNavigate()
  const [selectedStudentId, setSelectedStudentId] = useState<string>()
  const [selectedSchoolName, setSelectedSchoolName] = useState<string>()
  const [selectedStudentName, setSelectedStudentName] = useState<string>()
  const [selectedSchedule, setSelectedSchedule] = useState<string>()
  const [msg, setMsg] = useState<MessageProps>()
  const [isLoading, setIsLoading] = React.useState<string>('')

  // 2. Derived Constants
  const hasStudents: boolean = students?.length > 0

  // 3. Effect Hooks
  useEffect(() => {
    const student: StudentProps | undefined = students?.find((s: StudentProps) => s.studentId === studentIdFromQuery)
    setSelectedStudentId(studentIdFromQuery)
    setSelectedSchoolName(student?.schoolName ?? '')
    setSelectedStudentName(student?.studentName ?? '')
    setSelectedSchedule(student?.scheduleGroupName ?? '')
  }, [studentIdFromQuery, students])

  // 4. Derived State, Validations and Early Returns

  // 5. Event Handlers
  // Handle change school
  const handleSelectSchool = async (sn: string, sid: string, ssn: string, sgn: string) => {
    setSelectedSchoolName(sn)
    setSelectedStudentId(sid)
    setSelectedStudentName(ssn)
    setSelectedSchedule(sgn)
  }
  // Handle delete school
  const handleRemoveSchool = async (schoolName: string, studentId: string) => {
    setIsLoading('Removing school...')
    const { msg, error } = await deleteStudent(schoolName, studentId)
    if (error) setMsg(msg)
    else {
      // change the selected school if it was deleted
      if (studentId === studentIdFromQuery) await onRemove(studentId, () => navigate(`/registration`))
      else await onRemove(studentId)
    }
    setIsLoading('')
  }
  // handlers
  const onCancel = () => navigate(`/hub?id=${studentIdFromQuery}`)
  // Save button handler
  const onSubmit = async () => {
    if (selectedStudentId && selectedSchoolName && selectedSchedule) {
      setIsLoading('Saving...')
      const { msg, error } = await updateStudentSettings(
        selectedSchoolName,
        selectedStudentId,
        undefined,
        undefined,
        selectedStudentName,
        selectedSchedule,
      )
      if (error) setMsg(msg)
      else {
        await onSelect(selectedStudentId, () => navigate(`/registration?id=${selectedStudentId}`))
        setMsg({
          heading: 'Success',
          text: 'Student saved successfully',
          style: 'success',
        })
      }
      setIsLoading('')
    }
  }

  // 6. Render
  const body: JSX.Element = (
    <>
      {hasStudents ? (
        <div className={isWeb ? 'pt-5' : 'pt-2 px-5'} data-testid='school-testid'>
          <ButtonMobileComponent
            text={`Register New Student`}
            data_testid='register-new-student-testid'
            width={'auto'}
            href='/add'
            icon={<PlusIcon size={24} />}
          />
          <p className='text-gray-400 text-lg font-semibold pb-2 pt-4'>Select a student to update</p>
          {students.map((s: StudentProps, i: number) => {
            const {
              schoolName,
              schoolStats,
              schoolScheduleGroups,
              schoolAdminLink,
              studentId,
              studentName,
              scheduleGroupName,
            } = s
            const schoolScheduleGroupNames: string[] =
              schoolScheduleGroups?.map((group: any) => group.schoolScheduleGroupName) ?? []
            const isSelected: boolean = selectedStudentId === studentId

            return (
              <div
                key={i}
                className={`border p-2 rounded-xl w-full mb-4 transition-colors ${
                  isSelected ? 'border-custom-yellow border-l-8 shadow' : 'border-gray-200'
                }`}
              >
                <div className='flex items-center justify-between w-full'>
                  <div className='flex items-center mr-1'>
                    <CustomRadio
                      size='small'
                      checked={isSelected}
                      onChange={() => handleSelectSchool(schoolName, studentId, studentName ?? '', scheduleGroupName)}
                      value={studentId}
                    />
                    <div className={`${isSelected ? 'text-headings' : 'text-gray-400'} ml-2 text-base font-medium`}>
                      {titleCaseSchoolNameForUI(schoolName)}
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <div className='text-[12px] bg-light-yellow py-1 px-2 rounded-xl'>
                      {schoolStats?.registeredUsers} Families
                    </div>
                    <ModalConfirmRemoveComponent
                      data_testid='DeleteOutlinedIcon'
                      icon={<DeleteIcon />}
                      title='Delete Registered School?'
                      body={`You will no longer see ${titleCaseSchoolNameForUI(schoolName)}`}
                      actionText='Delete now'
                      onClick={() => handleRemoveSchool(schoolName, studentId)}
                    />
                  </div>
                </div>
                {isSelected && (
                  <div className='pt-4 flex flex-col gap-4'>
                    <InputMobileComponent
                      type='text'
                      labeltext='Student Name'
                      data_testid='student_name_testid'
                      isError={!isValidName(selectedStudentName)}
                      value={selectedStudentName}
                      onChange={setSelectedStudentName} // Update state when changed
                    />
                    {schoolScheduleGroupNames.length > 1 && (
                      <SelectMobileComponent
                        key='1c'
                        label='Select School Schedule'
                        isRequired={true}
                        value={selectedSchedule} // Use the state value
                        options={schoolScheduleGroupNames} // Options from the fetched data
                        onChange={setSelectedSchedule} // Update state when changed
                      />
                    )}
                    {schoolAdminLink && (
                      <div className='flex flex-row gap-2 justify-center'>
                        <WebsiteLinkIcon color='#474747' size={22} />
                        <p className='text-sm'>
                          <NavComponent
                            href={schoolAdminLink || '#'}
                            text={`${titleCaseSchoolNameForUI(schoolName)} Family Resources`}
                            isOpenNewTab={!!schoolAdminLink}
                          />
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div className='flex flex-col items-center justify-center p-8 text-center'>
          <AssignmentIndIcon size={64} color='gray' />
          <h2 className='text-2xl font-semibold mb-2'>No Students Registered</h2>
          <p className='text-gray-600 mb-6'>Get started by registering your first student to access all features.</p>
          <ButtonMobileComponent
            text='Register First Student'
            data_testid='register-first-student-testid'
            width={'auto'}
            href='/add'
            icon={<PlusIcon size={24} />}
          />
        </div>
      )}
    </>
  )
  const footer: JSX.Element = (
    <div className='fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 pt-3 pb-7 px-5'>
      <div className='flex justify-around gap-3'>
        <ButtonMobileComponent color='bg-btn-grey' text='Cancel' onClick={onCancel} />
        <ButtonMobileComponent iconPosition='right' icon={Arrow} onClick={onSubmit} text={'Save'} />
      </div>
    </div>
  )

  return (
    <>
      {/***************** Display progress and error *****************/}
      {isLoading && <LinearProgress />}
      {msg && (
        <ToastComponent style={msg?.style} heading={msg?.heading} text={msg?.text} onClose={() => setMsg(undefined)} />
      )}
      {/***************** Mobile *****************/}
      {!isWeb && (
        <div className='flex flex-col min-h-screen'>
          <div className='sticky top-0 z-10 bg-white'>
            <BackComponent text='Registration' onClick={onCancel} />
          </div>
          <div className='flex-1 pb-24'>{body}</div>
          {hasStudents && (
            <div className='fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200'>{footer}</div>
          )}
        </div>
      )}
      {/***************** Web *****************/}
      {isWeb && (
        <WizardComponent
          onBack={onCancel}
          onCancel={onCancel}
          title={'Registration'}
          onClick={hasStudents ? onSubmit : undefined}
          btnText='Save'
          data_testId='modal-dialog-action-submit'
        >
          {body}
        </WizardComponent>
      )}
    </>
  )
}

function Index(props: {
  students: StudentProps[] | null
  processQuery: (id: string) => string
  onUpdate: (cb?: () => void) => void
  onSelect: (ssi: string, cb?: () => void) => void
  onRemove: (ssi: string, cb?: () => void) => void
}): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()

  // Inputs: Selected Student Id, and Callback
  const { students, processQuery, onSelect, onRemove } = props

  // If we  navigate to this URL without a student id in query search parameters,
  // use our root app handler callback to find the student to display
  const studentId = searchParams.get('id') ?? ''
  useEffect(() => {
    setSearchParams({ id: processQuery(studentId) })
  }, [studentId, processQuery, setSearchParams])

  // Check if queried student exists
  const student: StudentProps | undefined = students?.find((s: StudentProps) => s.studentId === studentId)

  if (!students || (students.length > 0 && !student)) return <LinearProgress />
  else
    return (
      <RegistrationComponent
        studentIdFromQuery={studentId}
        students={students ?? []}
        onSelect={onSelect}
        onRemove={onRemove}
      />
    )
}

export default Index
